<template>
  <div>
    <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
      <div>
        <h4 class="text-sm font-semibold">Insights</h4>
        <div class="text-xs text-gray-500">AI generated insights for the selected survey scope.</div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <label class="text-xs text-gray-600">Segment</label>
        <select v-model="surveyInsightsSegmentByProxy" @change="actions.onSurveyInsightsSegmentByChange()" class="border rounded px-2 py-1 text-xs">
          <option value="ceremony">Ceremony</option>
          <option value="cohort">Cohort</option>
        </select>
        <button
          v-if="!state.surveyInsights"
          @click="actions.explainSurveyInsights()"
          class="px-3 py-2 rounded text-xs border inline-flex items-center gap-1.5 bg-purple-100 text-purple-900 border-purple-300 hover:bg-purple-200 disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="state.surveyInsightsLoading || state.surveyInsightsNarrativeLoading"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.539 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
          </svg>
          <span>{{ (state.surveyInsightsLoading || state.surveyInsightsNarrativeLoading) ? 'Explaining...' : 'Explain results' }}</span>
        </button>
        <button
          v-else
          @click="actions.regenerateSurveyInsights()"
          class="px-3 py-2 rounded text-xs border bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="state.surveyInsightsLoading || state.surveyInsightsNarrativeLoading"
        >
          Regenerate
        </button>
      </div>
    </div>

    <div class="text-xs text-gray-500 mb-3">
      Scope: {{ (state.surveySelectedCeremonyIds || []).length }} ceremon{{ (state.surveySelectedCeremonyIds || []).length === 1 ? 'y' : 'ies' }}
      <template v-if="state.surveySeasonKey !== 'all'"> in selected season.</template>
      <template v-else> across selected ceremonies.</template>
      <span class="ml-1">Segmenting by {{ state.surveyInsightsSegmentBy === 'cohort' ? 'cohort' : 'ceremony' }}.</span>
      <span v-if="state.surveyInsightsGeneratedAt" class="ml-1">
        Last generated {{ actions.formatInsightsGeneratedAt(state.surveyInsightsGeneratedAt) }}<template v-if="state.surveyInsightsLoadedFromCache"> (saved insight)</template>.
      </span>
    </div>

    <div v-if="state.surveyInsightsLoading" class="text-sm text-gray-500">Generating insights...</div>
    <div v-else-if="state.surveyInsightsError" class="text-sm text-red-600">{{ state.surveyInsightsError }}</div>
    <div v-else-if="!state.surveyInsights" class="text-sm text-gray-500">Click Explain results to generate ranked insights for the current scope.</div>
    <div v-else>
      <div v-if="state.surveyInsightsNarrativeLoading" class="text-xs text-gray-500 mb-3">Generating AI summary...</div>
      <div v-if="!state.surveyInsightsNarrativeHeadline.title && !state.surveyInsightsNarrativeInsights.length && !state.surveyInsightsNarrativeNextSteps.length" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
        AI insights are currently unavailable. Please try again.
      </div>
      <div v-else class="border rounded-lg bg-white p-4 md:p-5 mt-4 space-y-6">
        <div class="border border-purple-200 rounded-xl p-4 md:p-5 bg-gradient-to-br from-purple-50 to-white">
          <div class="text-sm font-semibold text-purple-900 mb-2">AI summary</div>
          <div v-if="state.surveyInsightsNarrativeHeadline.title" class="space-y-2">
            <div class="text-lg font-semibold leading-tight text-gray-900">{{ actions.formatInsightText(state.surveyInsightsNarrativeHeadline.title) }}</div>
            <div class="text-sm text-gray-700 break-words whitespace-pre-line">{{ actions.formatInsightText(state.surveyInsightsNarrativeHeadline.summary) }}</div>
            <div class="text-xs text-gray-500">{{ actions.formatInsightText(state.surveyInsightsNarrativeHeadline.scope_note) }}</div>
          </div>
          <div v-else class="text-sm text-gray-500">Summary unavailable for this scope.</div>

          <div v-if="state.surveyTopFindings.length" class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div v-for="(card, idx) in state.surveyTopFindings" :key="`narrative-top-finding-${actions.insightCardId(card, idx, 'top')}`" class="rounded-lg border border-purple-200 bg-white p-3">
              <button type="button" class="w-full text-left" @click="actions.toggleInsightCard(card, idx, 'top')">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <div class="text-[11px] uppercase tracking-wide text-purple-700 font-semibold">Top finding</div>
                  <span class="text-gray-500">{{ actions.isInsightCardExpanded(card, idx, 'top') ? '▾' : '▸' }}</span>
                </div>
                <div class="text-xl font-semibold text-gray-900 leading-none mb-1">{{ actions.formatInsightText(actions.insightPrimaryMetric(card).value || 'Signal detected') }}</div>
                <div class="text-[11px] text-gray-500 mb-2">{{ actions.formatInsightText(actions.insightPrimaryMetric(card).label || 'Primary metric') }}</div>
                <div class="text-xs text-gray-700 break-words">{{ actions.isInsightCardExpanded(card, idx, 'top') ? actions.formatInsightText(card.summary) : actions.compactInsightSummary(card.summary, 90) }}</div>
                <div class="text-[11px] mt-1 text-purple-700">{{ actions.isInsightCardExpanded(card, idx, 'top') ? 'Hide details ▾' : 'Show details ▸' }}</div>
              </button>
              <div v-if="actions.isInsightCardExpanded(card, idx, 'top')" class="mt-2">
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <span v-for="(chip, chipIdx) in (card.evidence || []).slice(0, 6)" :key="`narrative-top-evidence-${idx}-${chipIdx}`" class="text-[11px] px-2 py-1 rounded bg-gray-50 border text-gray-700">
                    {{ chip.label }}: {{ actions.formatInsightText(chip.value) }}
                  </span>
                </div>
                <button v-if="card.drilldown && card.drilldown.deep_link_key" @click="actions.applySurveyInsightLink(card)" class="text-xs text-purple-700 hover:underline">
                  {{ card.drilldown.label || 'View chart' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="state.surveyKeyInsights.length">
          <div class="text-sm font-semibold mb-3">Key insights</div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div v-for="(card, idx) in state.surveyKeyInsights" :key="`narrative-key-card-${actions.insightCardId(card, idx, 'key')}`" class="rounded-lg border p-3 md:p-4" :class="actions.insightTone(card).container">
              <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div class="text-[11px] uppercase tracking-wide font-semibold" :class="actions.insightTone(card).textStrong">{{ actions.insightCategoryLabel(card) }}</div>
                <div class="text-[11px] px-2 py-1 rounded border bg-white/90" :class="actions.insightTone(card).pill">{{ actions.insightConfidenceLabel(card) }}</div>
              </div>
              <div class="text-sm font-semibold text-gray-900 mb-1 break-words">{{ actions.formatInsightText(card.title) }}</div>
              <div class="text-2xl font-semibold text-gray-900 leading-none mb-1">{{ actions.formatInsightText(actions.insightPrimaryMetric(card).value || 'Signal detected') }}</div>
              <div class="text-[11px] text-gray-500 mb-2 break-words">{{ actions.formatInsightText(actions.insightPrimaryMetric(card).label || 'Primary metric') }}</div>
              <div class="h-1.5 rounded-full bg-white/80 border border-gray-100 overflow-hidden mb-2">
                <div class="h-full rounded-full transition-all duration-300" :style="{ width: `${actions.insightMetricBarPercent(actions.insightPrimaryMetric(card), card)}%`, backgroundColor: actions.insightTone(card).barColor }" />
              </div>
              <button type="button" class="w-full text-left mb-2" @click="actions.toggleInsightCard(card, idx, 'key')">
                <div class="text-xs text-gray-700 break-words">{{ actions.isInsightCardExpanded(card, idx, 'key') ? actions.formatInsightText(card.summary) : actions.compactInsightSummary(card.summary, 120) }}</div>
                <div class="text-[11px] mt-1" :class="actions.insightTone(card).textStrong">{{ actions.isInsightCardExpanded(card, idx, 'key') ? 'Hide details ▾' : 'Show details ▸' }}</div>
              </button>
              <div v-if="actions.isInsightCardExpanded(card, idx, 'key')">
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <span v-for="(chip, chipIdx) in (card.evidence || []).slice(0, 6)" :key="`narrative-key-evidence-${idx}-${chipIdx}`" class="text-[11px] px-2 py-1 rounded bg-gray-50 border text-gray-700">
                    {{ chip.label }}: {{ actions.formatInsightText(chip.value) }}
                  </span>
                </div>
                <button v-if="card.drilldown && card.drilldown.deep_link_key" @click="actions.applySurveyInsightLink(card)" class="text-xs hover:underline" :class="actions.insightTone(card).link">
                  {{ card.drilldown.label || 'View chart' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="state.surveyVisualPatterns.length">
          <div class="text-sm font-semibold mb-3">Visual patterns and comparisons</div>
          <div class="rounded-lg border bg-gray-50 p-3 md:p-4 space-y-3">
            <div v-for="(pattern, idx) in state.surveyVisualPatterns" :key="`insight-pattern-${pattern.id || idx}`" class="rounded-md bg-white border p-2.5">
              <div class="flex items-center justify-between gap-2 mb-1.5">
                <div class="text-xs font-medium text-gray-800 truncate">{{ actions.formatInsightText(pattern.title) }}</div>
                <div class="text-xs font-semibold text-gray-700 shrink-0">{{ actions.formatInsightText(pattern.metricValue) }}</div>
              </div>
              <div class="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div class="h-full rounded-full transition-all duration-300" :style="{ width: `${pattern.barPercent}%`, backgroundColor: pattern.barColor }" />
              </div>
              <div class="text-[11px] text-gray-500 mt-1">{{ pattern.metricLabel }}</div>
            </div>
          </div>
        </div>

        <div v-if="state.surveyDetailedInsights.length">
          <div class="text-sm font-semibold mb-3">Detailed insights</div>
          <div class="space-y-2">
            <div v-for="(card, idx) in state.surveyDetailedInsights" :key="`narrative-detailed-card-${actions.insightCardId(card, idx, 'detail')}`" class="border rounded-lg overflow-hidden">
              <button type="button" class="w-full px-3 py-2.5 text-left flex items-center justify-between gap-3 bg-white hover:bg-gray-50" @click="actions.toggleDetailedInsight(card, idx)">
                <div class="min-w-0">
                  <div class="text-sm font-medium truncate">{{ actions.formatInsightText(card.title) }}</div>
                  <div class="text-xs text-gray-500 truncate">{{ actions.compactInsightSummary(card.summary, 120) }}</div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <div class="text-[11px] px-2 py-1 rounded border bg-white" :class="actions.insightTone(card).pill">{{ actions.insightConfidenceLabel(card) }}</div>
                  <span class="text-gray-500">{{ actions.isDetailedInsightExpanded(card, idx) ? '▾' : '▸' }}</span>
                </div>
              </button>
              <div v-if="actions.isDetailedInsightExpanded(card, idx)" class="px-3 pb-3 bg-gray-50 border-t">
                <div class="text-xs text-gray-700 pt-2 mb-2">{{ actions.compactInsightSummary(card.summary, 260) }}</div>
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <span v-for="(chip, chipIdx) in (card.evidence || []).slice(0, 8)" :key="`narrative-detailed-evidence-${idx}-${chipIdx}`" class="text-[11px] px-2 py-1 rounded bg-white border text-gray-700">
                    {{ chip.label }}: {{ actions.formatInsightText(chip.value) }}
                  </span>
                </div>
                <button v-if="card.drilldown && card.drilldown.deep_link_key" @click="actions.applySurveyInsightLink(card)" class="text-xs text-blue-700 hover:underline">{{ card.drilldown.label || 'View chart' }}</button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="state.surveyInsightsRecommendedActions.length">
          <div class="text-sm font-semibold mb-3">Recommended actions</div>
          <div class="space-y-3">
            <div v-for="(step, idx) in state.surveyInsightsRecommendedActions" :key="`narrative-action-${actions.insightCardId(step, idx, 'action')}`" class="border border-indigo-200 bg-indigo-50 rounded-lg p-3 md:p-4">
              <div class="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div class="text-sm font-semibold text-indigo-900">{{ actions.formatInsightText(step.title) }}</div>
                <div class="text-[11px] px-2 py-1 rounded border border-indigo-300 bg-white text-indigo-800">{{ actions.insightConfidenceLabel(step) }}</div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs mb-2">
                <div class="rounded border border-indigo-200 bg-white p-2">
                  <div class="text-[11px] uppercase tracking-wide font-semibold text-indigo-700 mb-1">Issue or opportunity</div>
                  <div class="text-gray-700 break-words">{{ actions.formatInsightText(step.title) }}</div>
                </div>
                <div class="rounded border border-indigo-200 bg-white p-2">
                  <div class="text-[11px] uppercase tracking-wide font-semibold text-indigo-700 mb-1">Why it matters</div>
                  <div class="text-gray-700 break-words">{{ actions.formatInsightText(step.summary) }}</div>
                </div>
                <div class="rounded border border-indigo-200 bg-white p-2">
                  <div class="text-[11px] uppercase tracking-wide font-semibold text-indigo-700 mb-1">What to investigate</div>
                  <div class="text-gray-700 break-words">{{ actions.formatInsightText((step.drilldown && step.drilldown.label) || 'Review supporting chart for this signal') }}</div>
                </div>
              </div>
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <span v-for="(chip, chipIdx) in (step.evidence || []).slice(0, 6)" :key="`narrative-action-evidence-${idx}-${chipIdx}`" class="text-[11px] px-2 py-1 rounded bg-white border text-indigo-900">
                  {{ chip.label }}: {{ actions.formatInsightText(chip.value) }}
                </span>
              </div>
              <button v-if="step.drilldown && step.drilldown.deep_link_key" @click="actions.applySurveyInsightLink(step)" class="text-xs text-indigo-800 hover:underline">{{ step.drilldown.label || 'View chart' }}</button>
            </div>
          </div>
        </div>

        <div v-if="state.surveyInsightsNarrativeCaveats.length" class="pt-2 border-t">
          <button type="button" class="w-full flex items-center justify-between text-left text-xs font-semibold text-gray-700" @click="actions.toggleSurveyInsightsCaveats()">
            <span>Caveats</span>
            <span class="text-gray-500">{{ state.surveyInsightsCaveatsCollapsed ? '▸' : '▾' }}</span>
          </button>
          <div v-if="!state.surveyInsightsCaveatsCollapsed" class="mt-2">
            <div v-for="(caveat, idx) in state.surveyInsightsNarrativeCaveats" :key="`narrative-caveat-${idx}`" class="text-[11px] text-gray-600">{{ caveat }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SurveyInsightsSection',
  props: {
    state: { type: Object, required: true },
    actions: { type: Object, required: true },
  },
  computed: {
    surveyInsightsSegmentByProxy: {
      get() { return this.state.surveyInsightsSegmentBy },
      set(value) { this.$emit('update:surveyInsightsSegmentBy', value) },
    },
  },
}
</script>
